import {CopilotIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, FormControl, Label, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'

import {MarkdownEditor} from '@github-ui/markdown-editor'
import {testIdProps} from '@github-ui/test-id-props'

import type {UsePullRequestSummaryResult} from './use-pull-request-summary'

export interface PullRequestSummaryToolbarButtonProps {
  /** Shows the staff-only "Edit prompt" menu item and its dialog. */
  promptEditorEnabled: boolean
  summary: UsePullRequestSummaryResult
}

/**
 * Copilot toolbar button for the pull request description editor. Opens a menu with a
 * "Summary" action (generates a summary of the PR's diff) and, when enabled, a staff-only
 * "Edit prompt" action that opens a dialog to customize the prompt before generating.
 *
 * Reimplements `@github-ui/copilot-markdown-toolbar-button`'s `CopilotMarkdownToolbarButton`
 * UI as a self-contained React component (real button + ref, not a DOM `id` lookup) so it can
 * be composed directly into `CommentBox`'s toolbar.
 */
export function PullRequestSummaryToolbarButton({promptEditorEnabled, summary}: PullRequestSummaryToolbarButtonProps) {
  const {
    isGenerating,
    menuOpen,
    setMenuOpen,
    generateSummary,
    promptEditorOpen,
    openPromptEditor,
    closePromptEditor,
    customPrompt,
    setCustomPrompt,
    resetCustomPrompt,
    submitCustomPrompt,
  } = summary

  return (
    <>
      <ActionMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <ActionMenu.Anchor>
          <MarkdownEditor.ToolbarButton
            icon={CopilotIcon}
            aria-label="Copilot"
            aria-pressed={menuOpen}
            disabled={isGenerating}
            {...testIdProps('copilot-pr-summary-toolbar-button')}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="medium">
          <ActionList>
            <ActionList.Group>
              <ActionList.GroupHeading>Generate</ActionList.GroupHeading>
              <ActionList.Item
                onSelect={() => generateSummary()}
                disabled={isGenerating}
                {...testIdProps('copilot-pr-summary-btn')}
              >
                Summary
                <ActionList.Description variant="block">
                  Generate a summary of the changes in this pull request.
                </ActionList.Description>
              </ActionList.Item>
              {promptEditorEnabled && (
                <>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={openPromptEditor} disabled={isGenerating}>
                    Edit prompt
                    <ActionList.Description variant="block">
                      Customize the prompt before generating a summary.
                    </ActionList.Description>
                    <ActionList.TrailingVisual>
                      <Label variant="attention">Staff</Label>
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                </>
              )}
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      {promptEditorOpen && (
        <Dialog
          title={
            <>
              Edit prompt{' '}
              <Label variant="attention" className="ml-1">
                Staff
              </Label>
            </>
          }
          footerButtons={[
            {buttonType: 'default', content: 'Reset', onClick: resetCustomPrompt},
            {buttonType: 'primary', content: 'Continue', onClick: submitCustomPrompt},
          ]}
          onClose={closePromptEditor}
          {...testIdProps('copilot-prompt-editor')}
        >
          <FormControl id="copilot-prompt-textarea">
            <FormControl.Label>Prompt</FormControl.Label>
            <Textarea
              value={customPrompt}
              onChange={event => setCustomPrompt(event.target.value)}
              rows={12}
              block
              resize="vertical"
              autoFocus
              {...testIdProps('copilot-prompt-textarea')}
            />
          </FormControl>
        </Dialog>
      )}
    </>
  )
}
